import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { useDialog } from '@/providers/DialogProvider';
import { meQuery } from '@/queries/me.query';
import {
  disableAuthenticatorTwoFactorMutation,
  disableTwoFactorMutation,
  setupAuthenticatorTwoFactorMutation,
  setupTwoFactorMutation,
  verifyAuthenticatorTwoFactorSetupMutation,
  verifyTwoFactorSetupMutation,
} from '@/mutations/user.mutation';
import { ArrowLeft, Check, Copy, MobileSolid, ShieldCheckSolid } from '@mynaui/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { analytics } from '@/lib/analytics';
import QRCode from 'qrcode';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface Setup2FADialogProps {
  mobileNumber?: string | null;
  twoFactorEnabled?: boolean;
  twoFactorType?: 'SMS' | 'AUTHENTICATOR' | null;
}

export const Setup2FADialog: FunctionComponent<Setup2FADialogProps> = ({
  mobileNumber: existingMobileNumber,
  twoFactorEnabled,
  twoFactorType,
}) => {
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const { t } = useTranslation('account');

  const initialMethod = twoFactorEnabled ? (twoFactorType === 'AUTHENTICATOR' ? 'authenticator' : 'sms') : null;
  const [selectedMethod, setSelectedMethod] = useState<'authenticator' | 'sms' | null>(initialMethod);
  const [currentStep, setCurrentStep] = useState<'method' | 'setup'>(twoFactorEnabled ? 'setup' : 'method');
  const [stepNumber, setStepNumber] = useState(twoFactorEnabled ? 0 : 1);
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [mobileNumber, setMobileNumber] = useState(existingMobileNumber || '');
  const [isMobileNumberCorrect, setIsMobileNumberCorrect] = useState<boolean | null>(null);
  const [disableStep, setDisableStep] = useState<'confirm' | 'verify'>('confirm');
  const [disableSecret, setDisableSecret] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // Authenticator state
  const [authenticatorSecret, setAuthenticatorSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [authenticatorCode, setAuthenticatorCode] = useState('');
  const [isSettingUp, setIsSettingUp] = useState(false);

  const formik = useFormik({
    initialValues: {
      code: '',
      mobileNumber: '',
    },
    onSubmit: async (values) => {
      try {
        await execute(verifyTwoFactorSetupMutation, { secret: twoFactorSecret, code: values.code });
        analytics.capture('2FA Enabled', { method: 'sms' });
        toast.success(t('twoFactor.smsEnabled'));
        queryClient.invalidateQueries({ queryKey: [meQuery] });
        dismissActiveDialog();
      } catch (error) {
        toast.error(t('twoFactor.invalidCode'));
      }
    },
  });

  // Generate QR code when otpauth URI changes
  useEffect(() => {
    if (otpauthUri) {
      QRCode.toDataURL(otpauthUri, { width: 200, margin: 2 }).then(setQrCodeDataUrl);
    }
  }, [otpauthUri]);

  const handleMethodSelection = async (method: 'authenticator' | 'sms') => {
    setSelectedMethod(method);
    setCurrentStep('setup');
    setStepNumber(1);

    if (method === 'authenticator') {
      setIsSettingUp(true);
      try {
        const data = await execute(setupAuthenticatorTwoFactorMutation, {});
        setAuthenticatorSecret(data.setupAuthenticatorTwoFactor.secret);
        setOtpauthUri(data.setupAuthenticatorTwoFactor.otpauthUri);
      } catch (error) {
        toast.error(t('twoFactor.failedSetupAuthenticator'));
        setCurrentStep('method');
        setSelectedMethod(null);
      } finally {
        setIsSettingUp(false);
      }
    } else if (method === 'sms') {
      if (existingMobileNumber) {
        setIsMobileNumberCorrect(null);
      } else {
        setIsMobileNumberCorrect(false);
      }
    }
  };

  const sendSMSCode = async (phoneNumber: string) => {
    const toastId = toast.loading(t('twoFactor.sendingCode'));
    try {
      const data = await execute(setupTwoFactorMutation, { mobileNumber: phoneNumber });
      setTwoFactorSecret(data.setupTwoFactor.secret);
      setMobileNumber(phoneNumber);
      toast.success(t('twoFactor.codeSent'), { id: toastId });
      setStepNumber(2);
    } catch (error) {
      toast.error(t('twoFactor.failedSendCode'), { id: toastId });
    }
  };

  const handleSendDisableCode = async () => {
    if (!existingMobileNumber) return;
    const toastId = toast.loading(t('twoFactor.sendingCode'));
    try {
      const data = await execute(setupTwoFactorMutation, { mobileNumber: existingMobileNumber });
      setDisableSecret(data.setupTwoFactor.secret);
      setDisableStep('verify');
      toast.success(t('twoFactor.codeSent'), { id: toastId });
    } catch (error) {
      toast.error(t('twoFactor.failedSendCode'), { id: toastId });
    }
  };

  const handleDisable2FA = async () => {
    const toastId = toast.loading(t('twoFactor.disabling'));
    try {
      await execute(disableTwoFactorMutation, { secret: disableSecret, code: disableCode });
      analytics.capture('2FA Disabled', { method: 'sms' });
      toast.success(t('twoFactor.disabled'), { id: toastId });
      queryClient.invalidateQueries({ queryKey: [meQuery] });
      dismissActiveDialog();
    } catch (error) {
      toast.error(t('twoFactor.invalidCode'), { id: toastId });
    }
  };

  const handleDisableAuthenticator = async () => {
    const toastId = toast.loading(t('twoFactor.disabling'));
    try {
      await execute(disableAuthenticatorTwoFactorMutation, { code: disableCode });
      analytics.capture('2FA Disabled', { method: 'authenticator' });
      toast.success(t('twoFactor.disabled'), { id: toastId });
      queryClient.invalidateQueries({ queryKey: [meQuery] });
      dismissActiveDialog();
    } catch (error) {
      toast.error(t('twoFactor.invalidCode'), { id: toastId });
    }
  };

  const handleVerifyAuthenticator = async () => {
    const toastId = toast.loading(t('twoFactor.verifying'));
    try {
      await execute(verifyAuthenticatorTwoFactorSetupMutation, { secret: authenticatorSecret, code: authenticatorCode });
      analytics.capture('2FA Enabled', { method: 'authenticator' });
      toast.success(t('twoFactor.authenticatorEnabled'), { id: toastId });
      queryClient.invalidateQueries({ queryKey: [meQuery] });
      dismissActiveDialog();
    } catch (error) {
      toast.error(t('twoFactor.invalidCode'), { id: toastId });
    }
  };

  const handleMobileNumberConfirmation = (isCorrect: boolean) => {
    setIsMobileNumberCorrect(isCorrect);
    if (isCorrect && existingMobileNumber) {
      sendSMSCode(existingMobileNumber);
    }
  };

  const handleMobileNumberSubmit = () => {
    if (formik.values.mobileNumber) {
      sendSMSCode(formik.values.mobileNumber);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('twoFactor.copiedToClipboard'));
  };

  const goBack = () => {
    if (currentStep === 'setup') {
      if (stepNumber > 1) {
        setStepNumber((prev) => prev - 1);
      } else {
        setCurrentStep('method');
        setSelectedMethod(null);
        setStepNumber(1);
      }
    }
  };

  const renderMethodSelection = () => (
    <div className='space-y-4 select-none'>
      <div>
        <h3 className='text-md font-medium'>{t('twoFactor.setupTitle')}</h3>
        <p className='text-sm font-medium text-muted-foreground'>{t('twoFactor.chooseMethod')}</p>
      </div>
      <div className='grid grid-cols-2 gap-3'>
        <div
          className='relative flex flex-col items-center p-4 pt-8 text-center border border-border rounded-lg cursor-pointer transition-colors hover:border-slate-300 dark:hover:border-stone-600 hover:bg-slate-50 dark:hover:bg-stone-800 active:bg-slate-100 dark:active:bg-stone-700'
          onClick={() => handleMethodSelection('authenticator')}>
          <div className='size-20 rounded-full bg-blue-500/20 flex items-center justify-center mb-6'>
            <ShieldCheckSolid className='size-12 text-blue-500' />
          </div>
          <Badge variant='success' className='bg-slate-900 dark:bg-slate-100 dark:text-slate-900 hover:bg-slate-900 dark:hover:bg-slate-100 absolute top-2 left-2'>
            {t('twoFactor.recommended')}
          </Badge>
          <div className='flex flex-col gap-1 mt-3'>
            <h4 className='font-semibold leading-none'>{t('twoFactor.authenticatorApp')}</h4>
            <p className='text-sm font-medium text-muted-foreground'>{t('twoFactor.authenticatorAppDescription')}</p>
          </div>
        </div>
        <div
          className='flex flex-col items-center p-4 pt-8 text-center border border-border rounded-lg cursor-pointer transition-colors hover:border-slate-300 dark:hover:border-stone-600 hover:bg-slate-50 dark:hover:bg-stone-800 active:bg-slate-100 dark:active:bg-stone-700'
          onClick={() => handleMethodSelection('sms')}>
          <div className='size-20 rounded-full bg-blue-500/20 flex items-center justify-center mb-6'>
            <MobileSolid className='size-12 text-blue-500' />
          </div>
          <div className='flex flex-col gap-1 mt-auto'>
            <h4 className='font-semibold leading-none'>{t('twoFactor.sms')}</h4>
            <p className='text-sm font-medium text-muted-foreground'>{t('twoFactor.smsDescription')}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAuthenticatorDisableView = () => (
    <div className='space-y-4'>
      <div>
        <h3 className='text-md font-medium'>{t('twoFactor.twoFactorTitle')}</h3>
        <p className='text-sm font-medium text-muted-foreground'>{t('twoFactor.authenticatorEnabledDescription')}</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{disableStep === 'confirm' ? t('twoFactor.authenticatorAppCardTitle') : t('twoFactor.enterVerificationCode')}</CardTitle>
          <CardDescription>
            {disableStep === 'confirm'
              ? t('twoFactor.authenticatorAppProtected')
              : t('twoFactor.enterCodeFromApp')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {disableStep === 'confirm' ? (
            <Button variant='destructive' onClick={() => setDisableStep('verify')}>
              {t('twoFactor.disableTwoFactor')}
            </Button>
          ) : (
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='disableCode'>{t('twoFactor.verificationCode')}</Label>
                <Input
                  id='disableCode'
                  type='text'
                  placeholder='123456'
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  maxLength={6}
                  className='text-center text-lg tracking-widest'
                />
              </div>
              <div className='flex justify-end space-x-2'>
                <Button variant='outline' onClick={() => setDisableStep('confirm')}>
                  {t('twoFactor.back')}
                </Button>
                <Button variant='destructive' onClick={handleDisableAuthenticator} disabled={!disableCode}>
                  {t('twoFactor.confirmAndDisable')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderAuthenticatorSetup = () => {
    if (stepNumber === 0 && twoFactorEnabled) {
      return renderAuthenticatorDisableView();
    }

    if (stepNumber === 1) {
      return (
        <div className='space-y-4'>
          <div className='flex items-center space-x-2'>
            <Button variant='ghost' size='sm' onClick={goBack}>
              <ArrowLeft className='h-4 w-4' />
            </Button>
            <h3 className='text-lg font-semibold'>{t('twoFactor.setupAuthenticatorStep1')}</h3>
          </div>

          <div className='flex flex-col gap-1 text-center'>
            <CardTitle>{t('twoFactor.scanQrCode')}</CardTitle>
            <CardDescription>{t('twoFactor.scanQrCodeDescription')}</CardDescription>
          </div>
          <div className='flex flex-col gap-4 items-center'>
            {qrCodeDataUrl ? (
              <img src={qrCodeDataUrl} alt='QR Code' className='w-48 h-48 rounded-lg' />
            ) : (
              <div className='w-48 h-48 bg-muted border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center'>
                <div className='text-center text-muted-foreground text-sm'>{t('twoFactor.loading')}</div>
              </div>
            )}

            <div className='space-y-2 w-full'>
              <Label>{t('twoFactor.manualSetupKey')}</Label>
              <div className='flex items-center space-x-2'>
                <Input value={authenticatorSecret} readOnly className='font-mono text-sm' />
                <Button variant='outline' size='sm' onClick={() => copyToClipboard(authenticatorSecret)}>
                  <Copy className='h-4 w-4' />
                </Button>
              </div>
              <p className='text-xs text-muted-foreground'>
                {t('twoFactor.manualSetupKeyDescription')}
              </p>
            </div>
          </div>

          <div className='flex justify-end space-x-2'>
            <Button variant='outline' onClick={goBack}>
              {t('twoFactor.back')}
            </Button>
            <Button onClick={() => setStepNumber(2)} disabled={!authenticatorSecret}>
              {t('twoFactor.addedToApp')}
            </Button>
          </div>
        </div>
      );
    }

    if (stepNumber === 2) {
      return (
        <div className='space-y-4'>
          <div className='flex items-center space-x-2'>
            <Button variant='ghost' size='sm' onClick={goBack}>
              <ArrowLeft className='h-4 w-4' />
            </Button>
            <h3 className='text-lg font-semibold'>{t('twoFactor.setupAuthenticatorStep2')}</h3>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('twoFactor.enterVerificationCode')}</CardTitle>
              <CardDescription>{t('twoFactor.enterCodeDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='authenticatorCode'>{t('twoFactor.verificationCode')}</Label>
                  <Input
                    id='authenticatorCode'
                    type='text'
                    placeholder='123456'
                    value={authenticatorCode}
                    onChange={(e) => setAuthenticatorCode(e.target.value)}
                    maxLength={6}
                    className='text-center text-lg tracking-widest'
                  />
                </div>
                <div className='flex justify-end space-x-2'>
                  <Button type='button' variant='outline' onClick={goBack}>
                    {t('twoFactor.back')}
                  </Button>
                  <Button onClick={handleVerifyAuthenticator} disabled={!authenticatorCode || authenticatorCode.length < 6}>
                    {t('twoFactor.verifyAndComplete')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return null;
  };

  const renderDisableView = () => (
    <div className='space-y-4'>
      <div>
        <h3 className='text-md font-medium'>{t('twoFactor.twoFactorTitle')}</h3>
        <p className='text-sm font-medium text-muted-foreground'>{t('twoFactor.smsEnabledDescription')}</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{disableStep === 'confirm' ? t('twoFactor.smsVerification') : t('twoFactor.enterVerificationCode')}</CardTitle>
          <CardDescription>
            {disableStep === 'confirm'
              ? t('twoFactor.verificationCodesSentTo', { number: mobileNumber || existingMobileNumber })
              : t('twoFactor.codeSentTo', { number: mobileNumber || existingMobileNumber })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {disableStep === 'confirm' ? (
            <Button variant='destructive' onClick={handleSendDisableCode}>
              {t('twoFactor.disableTwoFactor')}
            </Button>
          ) : (
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='disableCode'>{t('twoFactor.verificationCode')}</Label>
                <Input
                  id='disableCode'
                  type='text'
                  placeholder='123456'
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  maxLength={6}
                  className='text-center text-lg tracking-widest'
                />
              </div>
              <div className='flex justify-end space-x-2'>
                <Button variant='outline' onClick={() => setDisableStep('confirm')}>
                  {t('twoFactor.back')}
                </Button>
                <Button variant='destructive' onClick={handleDisable2FA} disabled={!disableCode}>
                  {t('twoFactor.confirmAndDisable')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderSMSSetup = () => {
    if (stepNumber === 0 && twoFactorEnabled) {
      return renderDisableView();
    }

    if (stepNumber === 1) {
      if (isMobileNumberCorrect === null && existingMobileNumber) {
        return (
          <div className='space-y-4'>
            <div className='flex items-center space-x-2'>
              <Button variant='ghost' size='sm' onClick={goBack}>
                <ArrowLeft className='h-4 w-4' />
              </Button>
              <h3 className='text-lg font-semibold'>{t('twoFactor.setupSmsStep1')}</h3>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('twoFactor.confirmMobileNumber')}</CardTitle>
                <CardDescription>{t('twoFactor.confirmMobileNumberDescription')}</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='p-4 bg-muted rounded-lg'>
                  <p className='text-sm text-muted-foreground mb-2'>{t('twoFactor.currentMobileNumber')}</p>
                  <p className='font-mono text-lg'>{existingMobileNumber}</p>
                </div>
                <div className='space-y-2'>
                  <p className='text-sm text-muted-foreground'>{t('twoFactor.isNumberCorrect')}</p>
                  <div className='flex space-x-2'>
                    <Button variant='outline' onClick={() => handleMobileNumberConfirmation(true)} className='flex-1'>
                      <Check className='h-4 w-4 mr-2' />
                      {t('twoFactor.yesSendCode')}
                    </Button>
                    <Button variant='outline' onClick={() => handleMobileNumberConfirmation(false)} className='flex-1'>
                      {t('twoFactor.noChangeNumber')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      }

      if (isMobileNumberCorrect === false || !existingMobileNumber) {
        return (
          <div className='space-y-4'>
            <div className='flex items-center space-x-2'>
              <Button variant='ghost' size='sm' onClick={goBack}>
                <ArrowLeft className='h-4 w-4' />
              </Button>
              <h3 className='text-lg font-semibold'>{t('twoFactor.setupSmsStep1')}</h3>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('twoFactor.enterMobileNumber')}</CardTitle>
                <CardDescription>{t('twoFactor.enterMobileNumberDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleMobileNumberSubmit();
                  }}
                  className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='mobileNumber'>{t('twoFactor.mobileNumber')}</Label>
                    <Input
                      id='mobileNumber'
                      name='mobileNumber'
                      type='tel'
                      placeholder='+1234567890'
                      value={formik.values.mobileNumber}
                      onChange={formik.handleChange}
                    />
                  </div>
                  <div className='flex justify-end space-x-2'>
                    <Button type='button' variant='outline' onClick={goBack}>
                      {t('twoFactor.back')}
                    </Button>
                    <Button type='submit' disabled={!formik.values.mobileNumber}>
                      {t('twoFactor.sendCode')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        );
      }
    }

    if (stepNumber === 2) {
      return (
        <div className='space-y-4'>
          <div className='flex items-center space-x-2'>
            <Button variant='ghost' size='sm' onClick={goBack}>
              <ArrowLeft className='h-4 w-4' />
            </Button>
            <h3 className='text-lg font-semibold'>{t('twoFactor.setupSmsStep2')}</h3>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>{t('twoFactor.enterVerificationCode')}</CardTitle>
              <CardDescription>{t('twoFactor.codeSentToNumber', { number: mobileNumber })}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={formik.handleSubmit} className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='code'>{t('twoFactor.verificationCode')}</Label>
                  <Input
                    id='code'
                    name='code'
                    type='text'
                    placeholder='123456'
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    maxLength={6}
                    className='text-center text-lg tracking-widest'
                  />
                </div>
                <div className='flex justify-end space-x-2'>
                  <Button type='button' variant='outline' onClick={goBack}>
                    {t('twoFactor.back')}
                  </Button>
                  <Button type='submit' disabled={!formik.values.code || formik.isSubmitting}>
                    {formik.isSubmitting ? t('twoFactor.verifyingSubmit') : t('twoFactor.verifyAndComplete')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      );
    }

    return null;
  };

  return (
    <div className='space-y-4'>
      {currentStep === 'method' && !isSettingUp && renderMethodSelection()}
      {currentStep === 'method' && isSettingUp && (
        <div className='flex items-center justify-center py-8'>
          <div className='text-center text-muted-foreground text-sm'>{t('twoFactor.settingUpAuthenticator')}</div>
        </div>
      )}
      {currentStep === 'setup' && selectedMethod === 'authenticator' && renderAuthenticatorSetup()}
      {currentStep === 'setup' && selectedMethod === 'sms' && renderSMSSetup()}
    </div>
  );
};
